import React, { useEffect, useMemo, useState } from 'react';
import GrowthShell from '../../components/growth/GrowthShell';
import { riyadhDay } from '../../lib/growthRange';
import {
  COMMISSION_STATUSES, DEAL_SOURCES, DEAL_STAGES, dealTotals, fetchDeals, labelOf, removeDeal, saveDeal,
} from '../../lib/growthDirector';

// Growth OS «الصفقات خارج التطبيق» — owner, 2026-11-02. Managed deals closed
// outside the app, so profit and break-even count them. The client's name is
// for the owner only: the AI phase reads the deal number, never the name.

const fmt = (n) => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const EMPTY = () => ({ client_name: '', deal_value_sar: '', commission_sar: '', commission_status: 'pending', source: 'whatsapp', stage: 'lead', opened_on: riyadhDay(), closed_on: '', notes: '' });

function Select({ value, onChange, pairs, label }) {
  return (
    <label className="gos-sfield"><span>{label}</span>
      <select className="gos-sinput" value={value} onChange={(e) => onChange(e.target.value)}>
        {pairs.map(([k, a]) => <option key={k} value={k}>{a}</option>)}
      </select>
    </label>
  );
}

function DealForm({ deal, onDone }) {
  const [d, setD] = useState(deal);
  const [state, setState] = useState(null);
  const set = (k) => (v) => setD({ ...d, [k]: v });
  const ok = d.client_name.trim() !== '';
  const save = async () => {
    setState('…');
    try { await saveDeal(d); onDone(); } catch (e) { setState(`تعذّر الحفظ: ${e?.message || e}`); }
  };
  const del = async () => {
    if (!window.confirm(`حذف الصفقة ${deal.deal_no}؟`)) return;
    try { await removeDeal(deal.id); onDone(); } catch (e) { setState(`تعذّر الحذف: ${e?.message || e}`); }
  };
  return (
    <div className="gos-card" data-deal-form={deal.id ? deal.deal_no : 'new'} style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>{deal.id ? `تعديل ${deal.deal_no}` : 'صفقة جديدة'}</h3></div>
      <div className="gos-srow" style={{ borderTop: 0 }}>
        <input className="gos-sinput name" placeholder="العميل" value={d.client_name} onChange={(e) => set('client_name')(e.target.value)} aria-label="العميل" />
        <label className="gos-sfield"><span>قيمة الصفقة</span><input className="gos-sinput" type="number" min="0" step="any" dir="ltr" value={d.deal_value_sar ?? ''} onChange={(e) => set('deal_value_sar')(e.target.value)} /><span>ر.س</span></label>
        <label className="gos-sfield"><span>العمولة</span><input className="gos-sinput" type="number" min="0" step="any" dir="ltr" value={d.commission_sar ?? ''} onChange={(e) => set('commission_sar')(e.target.value)} /><span>ر.س</span></label>
        <Select label="العمولة" value={d.commission_status} onChange={set('commission_status')} pairs={COMMISSION_STATUSES} />
      </div>
      <div className="gos-srow">
        <Select label="المرحلة" value={d.stage} onChange={set('stage')} pairs={DEAL_STAGES} />
        <Select label="المصدر" value={d.source} onChange={set('source')} pairs={DEAL_SOURCES} />
        <label className="gos-sfield"><span>بدأت</span><input className="gos-sinput" style={{ width: 140 }} type="date" dir="ltr" value={d.opened_on || ''} onChange={(e) => set('opened_on')(e.target.value)} /></label>
        <label className="gos-sfield"><span>أُغلقت</span><input className="gos-sinput" style={{ width: 140 }} type="date" dir="ltr" value={d.closed_on || ''} onChange={(e) => set('closed_on')(e.target.value)} /></label>
      </div>
      <div className="gos-srow">
        <input className="gos-sinput note" placeholder="ملاحظات" value={d.notes || ''} onChange={(e) => set('notes')(e.target.value)} aria-label="ملاحظات" />
        <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} disabled={!ok} onClick={save}>حفظ</button>
        <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={() => onDone(true)}>إلغاء</button>
        {deal.id && <button type="button" className="gos-btn ghost" style={{ flex: '0 0 auto' }} onClick={del}>حذف</button>}
        {state && <span className="gos-faint">{state}</span>}
      </div>
    </div>
  );
}

export default function GrowthDeals({ user, profile, lang }) {
  const [deals, setDeals] = useState(undefined);
  const [editing, setEditing] = useState(null);
  const load = () => fetchDeals().then(setDeals).catch((e) => { console.warn('[GrowthDeals]', e?.message || e); setDeals(null); });
  useEffect(() => { load(); }, []);
  const t = useMemo(() => dealTotals(deals || []), [deals]);
  const done = (cancelled) => { setEditing(null); if (!cancelled) load(); };

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="deals" title="الصفقات خارج التطبيق"
                 actions={!editing && <button type="button" className="gos-btn" style={{ flex: '0 0 auto' }} onClick={() => setEditing(EMPTY())}>+ صفقة</button>}>
      <div className="gos-hello">
        <h2>الصفقات خارج التطبيق</h2>
        <p>الطلبات المُدارة التي تمت بالتواصل المباشر خارج التطبيق، لتدخل في الربح ونقطة التعادل. اسم العميل يبقى لك وحدك.</p>
      </div>
      <div className="gos-kpis gos-kpis-4" style={{ marginBottom: 16 }}>
        {[['عمولة مستلمة', t.received], ['عمولة مؤكدة', t.confirmed], ['عمولة متوقعة', t.pending], ['قيمة الصفقات', t.value]].map(([l, v]) => (
          <div key={l} className="gos-kpi" data-kpi={l}><p className="gos-kpi-label">{l}</p><p className="gos-kpi-value">{fmt(v)}</p><div className="gos-kpi-foot"><span className="gos-faint">ر.س · بدون الصفقات التي لم تتم</span></div></div>
        ))}
      </div>
      {editing && <DealForm key={editing.id || 'new'} deal={editing} onDone={done} />}
      <section className="gos-card" data-section="deals">
        <div className="gos-card-head"><h3>الصفقات</h3><span className="gos-pcount">{deals ? deals.length : '…'}</span></div>
        {deals === null && <div className="gos-empty-state"><strong>تعذّر التحميل</strong>أعد تحميل الصفحة.</div>}
        {deals && !deals.length && <div className="gos-empty-state"><strong>لا توجد صفقات بعد</strong>أضف أول صفقة من زر «+ صفقة».</div>}
        {deals && deals.length > 0 && (
          <div className="gos-table-scroll">
            <table className="gos-table" data-deals="1">
              <thead><tr><th>الرقم</th><th>العميل</th><th>المرحلة</th><th>القيمة</th><th>العمولة</th><th>حالة العمولة</th><th>المصدر</th><th>بدأت</th></tr></thead>
              <tbody>{deals.map((d) => (
                <tr key={d.id} className="gos-click" tabIndex={0} data-deal={d.deal_no}
                    onClick={() => setEditing({ ...d, closed_on: d.closed_on || '', notes: d.notes || '' })}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditing({ ...d, closed_on: d.closed_on || '', notes: d.notes || '' }); }}>
                  <td dir="ltr" style={{ textAlign: 'right' }}>{d.deal_no}</td>
                  <td>{d.client_name}</td>
                  <td>{labelOf(DEAL_STAGES, d.stage)}</td>
                  <td className="num">{fmt(d.deal_value_sar)}</td>
                  <td className="num">{fmt(d.commission_sar)}</td>
                  <td>{labelOf(COMMISSION_STATUSES, d.commission_status)}</td>
                  <td>{labelOf(DEAL_SOURCES, d.source)}</td>
                  <td className="gos-when">{d.opened_on}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </GrowthShell>
  );
}
